export type ScreenId = 'listing' | 'product-detail' | 'customize' | 'checkout';

export type RouteParams = Record<string, string>;

export interface NavigateOptions {
  replace?: boolean;
  params?: RouteParams;
}

export interface ScreenContext {
  el: HTMLElement;
  go: (id: ScreenId, opts?: NavigateOptions) => void;
  back: () => void;
  /** Active route params for this screen instance. */
  params: () => RouteParams;
}

export interface Screen {
  id: ScreenId;
  mount: (ctx: ScreenContext) => void | Promise<void>;
  unmount?: () => void;
  /** Called every time the screen becomes the top of the stack again. */
  onEnter?: (params: RouteParams) => void | Promise<void>;
  /** Called when the screen is pushed behind another screen. */
  onLeave?: () => void;
}

interface MountedScreen {
  screen: Screen;
  el: HTMLElement;
}

interface StackFrame {
  id: ScreenId;
  params: RouteParams;
}

interface RouteDef {
  id: ScreenId;
  /** Regex with named capture groups for params. */
  pattern: RegExp;
  /** Build a URL given params. */
  build: (params: RouteParams) => string;
  /** Parents in the stack (excluding self), in order from root to self. */
  parents: ScreenId[];
}

const ROUTES: RouteDef[] = [
  {
    id: 'listing',
    pattern: /^\/?$/,
    build: () => '/',
    parents: [],
  },
  {
    id: 'product-detail',
    pattern: /^\/p\/(?<productId>[^/]+)\/?$/,
    build: (p) => `/p/${encodeURIComponent(p.productId ?? '')}`,
    parents: ['listing'],
  },
  {
    id: 'customize',
    pattern: /^\/p\/(?<productId>[^/]+)\/customize\/?$/,
    build: (p) => `/p/${encodeURIComponent(p.productId ?? '')}/customize`,
    parents: ['listing', 'product-detail'],
  },
  {
    id: 'checkout',
    pattern: /^\/checkout\/(?<itineraryId>[^/]+)\/?$/,
    build: (p) => `/checkout/${encodeURIComponent(p.itineraryId ?? '')}`,
    parents: ['listing'],
  },
];

function matchPath(pathname: string): { def: RouteDef; params: RouteParams } | null {
  for (const def of ROUTES) {
    const m = def.pattern.exec(pathname);
    if (m) {
      return { def, params: { ...(m.groups ?? {}) } };
    }
  }
  return null;
}

function getRoute(id: ScreenId): RouteDef {
  const def = ROUTES.find((r) => r.id === id);
  if (!def) throw new Error(`Unknown route id: ${id}`);
  return def;
}

export class Router {
  private root: HTMLElement;
  private screens = new Map<ScreenId, Screen>();
  private mounted = new Map<ScreenId, MountedScreen>();
  private stack: StackFrame[] = [];
  /** Last-known params per screen id (so re-mounts know what to render). */
  private lastParams = new Map<ScreenId, RouteParams>();

  constructor(root: HTMLElement) {
    this.root = root;
    window.addEventListener('popstate', (e) => {
      const stateStack = (e.state as { stack?: StackFrame[] } | null)?.stack;
      void this.syncFromUrl(stateStack);
    });
  }

  register(screen: Screen): void {
    this.screens.set(screen.id, screen);
  }

  async start(fallback: ScreenId): Promise<void> {
    const match = matchPath(window.location.pathname);
    if (match) {
      const stack = this.buildStackForRoute(match.def.id, match.params);
      await this.replaceStack(stack, { replace: true });
      return;
    }
    const def = getRoute(fallback);
    const stack: StackFrame[] = [{ id: fallback, params: {} }];
    history.replaceState({ stack }, '', def.build({}));
    await this.replaceStack(stack, { replace: true, skipHistory: true });
  }

  /** Public navigation API used from screens. */
  async go(id: ScreenId, opts: NavigateOptions = {}): Promise<void> {
    const params = opts.params ?? {};
    const def = getRoute(id);
    const url = def.build(params);

    const next: StackFrame[] = opts.replace
      ? [...this.stack.slice(0, -1), { id, params }]
      : [...this.stack, { id, params }];

    if (opts.replace) {
      history.replaceState({ stack: next }, '', url);
    } else {
      history.pushState({ stack: next }, '', url);
    }
    await this.replaceStack(next, { replace: opts.replace, skipHistory: true });
  }

  async back(): Promise<void> {
    if (this.stack.length <= 1) return;
    history.back();
  }

  currentId(): ScreenId | undefined {
    return this.stack[this.stack.length - 1]?.id;
  }

  currentParams(): RouteParams {
    return this.stack[this.stack.length - 1]?.params ?? {};
  }

  /** Reconstruct stack purely from the URL (used on popstate without state). */
  private buildStackForRoute(id: ScreenId, params: RouteParams): StackFrame[] {
    const def = getRoute(id);
    const parents: StackFrame[] = def.parents.map((pid) => ({
      id: pid,
      params: pid === 'listing' ? {} : params,
    }));
    return [...parents, { id, params }];
  }

  private async syncFromUrl(stateStack?: StackFrame[]): Promise<void> {
    if (stateStack && stateStack.length > 0) {
      await this.replaceStack(stateStack, { skipHistory: true });
      return;
    }
    const match = matchPath(window.location.pathname);
    if (!match) {
      await this.replaceStack([{ id: 'listing', params: {} }], { skipHistory: true });
      return;
    }
    const next = this.buildStackForRoute(match.def.id, match.params);
    await this.replaceStack(next, { skipHistory: true });
  }

  private async replaceStack(
    next: StackFrame[],
    opts: { replace?: boolean; skipHistory?: boolean } = {},
  ): Promise<void> {
    void opts.replace; // history is already updated by callers
    void opts.skipHistory;
    const prevTop = this.currentId();
    for (const frame of next) {
      await this.ensureMounted(frame.id);
      this.lastParams.set(frame.id, frame.params);
    }
    this.stack = next;
    const newTop = this.currentId();
    if (prevTop && prevTop !== newTop) {
      this.screens.get(prevTop)?.onLeave?.();
    }
    this.applyStackClasses();
    if (newTop) {
      await this.screens.get(newTop)?.onEnter?.(this.currentParams());
    }
  }

  private async ensureMounted(id: ScreenId): Promise<void> {
    if (this.mounted.has(id)) return;
    const screen = this.screens.get(id);
    if (!screen) {
      throw new Error(`Unknown screen: ${id}`);
    }
    const el = document.createElement('div');
    el.className = 'screen';
    el.id = id;
    this.root.appendChild(el);
    this.mounted.set(id, { screen, el });
    await screen.mount({
      el,
      go: (next, navOpts) => {
        void this.go(next, navOpts);
      },
      back: () => {
        void this.back();
      },
      params: () => this.lastParams.get(id) ?? {},
    });
  }

  private applyStackClasses(): void {
    const topIndex = this.stack.length - 1;
    for (const { screen, el } of this.mounted.values()) {
      const idxInStack = this.stack.findIndex((f) => f.id === screen.id);
      el.classList.remove('s-active', 's-behind');
      if (idxInStack === topIndex) {
        el.classList.add('s-active');
      } else if (idxInStack >= 0 && idxInStack < topIndex) {
        el.classList.add('s-behind');
      }
    }
  }
}
